import { useMutation, useQueryClient } from "@tanstack/react-query";
import { wishlistApi, type FavListCollection } from "@/lib/wishlist-api";
import { wishlistQueryKeys } from "./use-wishlist-queries";

export const useCreateWishlistMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: wishlistApi.createList,
    onSuccess: (newList, variables) => {
      queryClient.setQueryData<FavListCollection[]>(wishlistQueryKeys.list(variables.customerId), (old) => {
        if (!old) return [newList];

        const filteredOld = old.filter((list) => !list.id.startsWith("temp-"));
        const existingIndex = filteredOld.findIndex((list) => list.id === newList.id);

        if (existingIndex >= 0) {
          const updated = [...filteredOld];
          updated[existingIndex] = newList;
          return updated;
        }
        return [...filteredOld, newList];
      });
    },
    onError: (err, variables) => {
      queryClient.invalidateQueries({ queryKey: wishlistQueryKeys.list(variables.customerId) });
    },
  });
};

export const useAddToWishlistMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ wishlistId, productId }: { wishlistId: string; productId: string }) =>
      wishlistApi.addToList(wishlistId, productId),
    onSuccess: (updatedList) => {
      queryClient.setQueryData<FavListCollection[]>(wishlistQueryKeys.list(updatedList.customerId), (old) => {
        if (!old) return [updatedList];
        return old.map((list) => (list.id === updatedList.id ? updatedList : list));
      });
    },
  });
};

export const useRemoveFromWishlistMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      wishlistItemId,
    }: {
      wishlistItemId: string;
      wishlistId: string;
      productId: string;
      customerId: string;
    }) => wishlistApi.removeFromList(wishlistItemId),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<FavListCollection[]>(wishlistQueryKeys.list(variables.customerId), (old) => {
        if (!old) return old;
        return old.map((list) =>
          list.id === variables.wishlistId
            ? {
                ...list,
                products: list.products.filter((product) => product.productId !== variables.productId),
              }
            : list
        );
      });
    },
  });
};

export const useDeleteWishlistMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ wishlistId }: { wishlistId: string; customerId: string }) => wishlistApi.deleteList(wishlistId),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<FavListCollection[]>(wishlistQueryKeys.list(variables.customerId), (old) => {
        if (!old) return old;
        return old.filter((list) => list.id !== variables.wishlistId);
      });
    },
  });
};

export const useRenameWishlistMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ wishlistId, newName }: { wishlistId: string; newName: string; customerId: string }) =>
      wishlistApi.renameList(wishlistId, newName),

    onSuccess: (updatedList, variables) => {
      queryClient.setQueryData<FavListCollection[]>(wishlistQueryKeys.list(variables.customerId), (old) => {
        if (!old) return [updatedList];
        return old.map((list) => (list.id === updatedList.id ? updatedList : list));
      });
    },

    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: wishlistQueryKeys.list(variables.customerId),
      });
    },
  });
};

export const useUpdateWishlistQuantityMutation = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      wishlistItemId,
      quantity,
    }: {
      wishlistItemId: string;
      quantity: number;
      wishlistId: string;
      customerId: string;
    }) => {
      return await wishlistApi.updateQuantity(wishlistItemId, quantity);
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: wishlistQueryKeys.list(variables.customerId) });

      // Snapshot previous value
      const previousLists = queryClient.getQueryData<FavListCollection[]>(wishlistQueryKeys.list(variables.customerId));

      // Optimistically update
      const updatedLists = previousLists
        ? previousLists.map((list) => {
            const clonedProducts = list.products.map((p) => ({ ...p }));
            if (list.id === variables.wishlistId) {
              return {
                ...list,
                products: clonedProducts.map((product) =>
                  product.id === variables.wishlistItemId ? { ...product, quantity: variables.quantity } : product
                ),
              };
            }
            return { ...list, products: clonedProducts };
          })
        : previousLists;

      queryClient.setQueryData<FavListCollection[]>(wishlistQueryKeys.list(variables.customerId), updatedLists);

      return { previousLists };
    },
    onError: (error: Error, variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        queryClient.setQueryData<FavListCollection[]>(
          wishlistQueryKeys.list(variables.customerId),
          context.previousLists
        );
      }
      options?.onError?.(error);
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
  });

  const debouncedUpdate = (wishlistItemId: string, quantity: number, wishlistId: string, customerId: string) => {
    mutation.mutate({ wishlistItemId, quantity, wishlistId, customerId });
  };

  return {
    updateQuantity: debouncedUpdate,
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};
